/**
 * What a usage counter reads as on screen — the meter's words and numbers, pure, so the
 * attention card and the billing screen state one thing one way.
 */
import {
  FAIR_USE_DEGRADE_TIERS,
  PLANS,
  meterState,
  usageWindowBounds,
  type MeterState,
  type RolledUpMetric,
  type Tier,
  type UsageView,
} from "@packagepage/shared";

import { dayName } from "./billing-states.js";

/** One counter as `usage.current` answers it. */
export interface UsageCounter {
  readonly metric: RolledUpMetric;
  readonly value: number;
  readonly limit: number | null;
}

export const METER_LABELS: Readonly<Record<RolledUpMetric, string>> = {
  gtins_active: "Active GTINs",
  resolver_requests: "Resolver requests",
};

/**
 * Under each meter, what it counts and what its limit does — `designs/Screen · Billing and Plan
 * States.dc.html`, and the ruling of 2026-10-28: past fair use, Growth and above are counted
 * and never charged or switched over.
 */
export function meterNote(
  metric: RolledUpMetric,
  plan: Tier,
  /** A plan change inside the window, which the count starts from — `usageFloor`. */
  countedFrom: Date | null = null,
): string {
  if (metric === "gtins_active") {
    return "Counted as products with a published page. Drafts and archived products don't count.";
  }
  const counted =
    countedFrom === null
      ? "Every scan and every machine request this month."
      : `Every scan and every machine request since ${dayName(countedFrom)}, when your plan changed.`;
  const ceiling = PLANS[plan].fairUsePerMonth;
  if (ceiling === null) return counted;
  if (FAIR_USE_DEGRADE_TIERS.includes(plan)) {
    return (
      `${counted} Past ${count(ceiling)}, scans land on our pages instead of yours until ` +
      "the count resets at the start of next month. Machine requests never change."
    );
  }
  return `${counted} Past the limit every scan keeps being answered, with no charge and no throttle.`;
}

/** A plan change inside the window that `resolver_requests` counts from, or `null` — Q-P16. */
export function countedFromOf(usage: UsageView): Date | null {
  const floor = new Date(usage.requestsCountedFrom);
  return floor.getTime() > usageWindowBounds(usage.window).start.getTime() ? floor : null;
}

/** Meters render in this order, GTINs first, as the mock draws them. */
export const METER_ORDER: readonly RolledUpMetric[] = [
  "gtins_active",
  "resolver_requests",
];

export function count(n: number): string {
  return n.toLocaleString("en-US");
}

/** Whole percent, rounded down, so the figure never reads 80 below the 80% line. */
export function percentOf(value: number, limit: number): number {
  return Math.floor((value * 100) / limit);
}

export interface MeterReading extends UsageCounter {
  readonly state: MeterState;
  /** `820 / 1,000`, or `128,400` where the plan has no figure. */
  readonly figure: string;
  /** 0–100, for the fill. */
  readonly fill: number;
}

export function readMeter(counter: UsageCounter): MeterReading {
  const { value, limit } = counter;
  return {
    ...counter,
    state: meterState(value, limit),
    figure: limit === null ? count(value) : `${count(value)} / ${count(limit)}`,
    fill: limit === null || limit <= 0 ? 0 : Math.min(100, percentOf(value, limit)),
  };
}
